import { User, Menu, Calendar, LogOut, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "@/hooks/use-toast";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { LazyProfileEditModal } from "@/components/LazyModals";

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const { isAuthenticated, profile, signOut, user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // 하단 GNB가 표시되는 페이지 (홈, 코스검색, 마이, 숙소검색 - 정확히 일치만)
  const bottomNavPages = ['/', '/courses', '/my', '/stay'];
  const hasBottomNav = bottomNavPages.includes(location.pathname);

  // GNB가 없는 페이지에서만 햄버거 메뉴와 뒤로가기 표시
  const showMobileMenu = !hasBottomNav;
  const showBackButton = !hasBottomNav && location.pathname !== '/';

  const handleSignOut = async () => {
    try {
      await signOut();
      toast({
        title: "로그아웃 완료",
        description: "성공적으로 로그아웃되었습니다.",
      });
    } catch (error) {
      toast({
        title: "로그아웃 실패",
        description: "로그아웃 중 오류가 발생했습니다.",
        variant: "destructive"
      });
    }
  };

  return (
    <header className="bg-white shadow-sm border-b sticky top-0 z-50 pt-safe-top">
      <div className="container mx-auto px-4 py-3">
        <div className="flex items-center justify-between">
          {/* Back Button + Logo */}
          <div className="flex items-center gap-2">
            {/* Mobile Back Button */}
            {showBackButton && (
              <button
                onClick={() => navigate(-1)}
                className="md:hidden flex items-center justify-center w-10 h-10 -ml-2 text-gray-600 hover:text-[#F39800] transition-colors"
                aria-label="뒤로가기"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
            )}
            <Link
              to="/"
              className="flex items-center space-x-2"
              onClick={() => {
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
            >
              <img src="/images/logo.svg" alt="홀씨" className="h-8 w-auto" />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
          </nav>

          {/* Desktop Auth Section */}
          <div className="hidden md:flex items-center space-x-4">
            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="ghost" 
                    className="flex items-center space-x-2 text-gray-700 hover:text-[#F39800] hover:bg-gray-50"
                    aria-label={`${profile?.name || '사용자'}님 안녕하세요`}
                    data-testid="user-menu-trigger"
                  >
                    <Avatar className="h-8 w-8">
                      {(profile?.avatar_url || user?.user_metadata?.avatar_url) && (
                        <AvatarImage 
                          src={profile?.avatar_url || user?.user_metadata?.avatar_url} 
                          alt={profile?.name || '프로필'}
                        />
                      )}
                      <AvatarFallback className="bg-[#F39800] text-white text-sm">
                        {profile?.name ? profile.name.charAt(0).toUpperCase() : 'U'}
                      </AvatarFallback>
                    </Avatar>
                    <span className="text-sm font-medium">{profile?.name || '사용자'}님 안녕하세요</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuItem asChild>
                    <Link to="/booking-history" className="flex items-center cursor-pointer">
                      <Calendar className="mr-2 h-4 w-4" />
                      예약 내역
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setIsProfileModalOpen(true)} className="cursor-pointer">
                    <User className="mr-2 h-4 w-4" />
                    프로필 설정
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleSignOut} className="cursor-pointer">
                    <LogOut className="mr-2 h-4 w-4" />
                    로그아웃
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button 
                className="bg-gradient-to-r from-[#F39800] to-[#E08700] hover:from-[#E08700] hover:to-[#D07600] text-white font-semibold px-6 py-2 rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105" 
                asChild
              >
                <Link to="/auth" className="flex items-center space-x-2">
                  <User className="h-4 w-4" />
                  <span>홀씨와 함께 시작하기</span>
                </Link>
              </Button>
            )}
          </div>

          {/* Mobile Menu Button */}
          {showMobileMenu && (
            <button
              className="md:hidden p-2"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label="메뉴 토글"
              data-testid="mobile-menu-button"
            >
              <Menu className="h-6 w-6 text-gray-700" />
            </button>
          )}
        </div>

        {/* Mobile Menu */}
        {showMobileMenu && isMenuOpen && (
          <div className="md:hidden mt-4 pb-4 border-t">
            <nav className="flex flex-col space-y-3 mt-4">
              <div className="flex flex-col space-y-2 pt-3 border-t">
                {isAuthenticated ? (
                  <>
                    <div className="flex items-center space-x-2 px-3 py-2">
                      <Avatar className="h-6 w-6">
                        {(profile?.avatar_url || user?.user_metadata?.avatar_url) && (
                          <AvatarImage 
                            src={profile?.avatar_url || user?.user_metadata?.avatar_url} 
                            alt={profile?.name || '프로필'}
                          />
                        )}
                        <AvatarFallback className="bg-[#F39800] text-white text-xs">
                          {profile?.name ? profile.name.charAt(0).toUpperCase() : 'U'}
                        </AvatarFallback>
                      </Avatar>
                      <span className="text-gray-700 text-sm">{profile?.name || '사용자'}님 안녕하세요</span>
                    </div>
                    <Button variant="ghost" className="text-gray-700 hover:text-[#F39800] justify-start" asChild>
                      <Link to="/booking-history" className="flex items-center">
                        <Calendar className="mr-2 h-4 w-4" />
                        예약 내역
                      </Link>
                    </Button>
                    <Button variant="ghost" className="text-gray-700 hover:text-[#F39800] justify-start" onClick={() => setIsProfileModalOpen(true)}>
                      <User className="mr-2 h-4 w-4" />
                      프로필 설정
                    </Button>
                    <Button variant="ghost" className="text-gray-700 hover:text-[#F39800] justify-start" onClick={handleSignOut}>
                      <LogOut className="mr-2 h-4 w-4" />
                      로그아웃
                    </Button>
                  </>
                ) : (
                  <Button 
                    className="bg-gradient-to-r from-[#F39800] to-[#E08700] hover:from-[#E08700] hover:to-[#D07600] text-white font-semibold justify-start" 
                    asChild
                  >
                    <Link to="/auth" className="flex items-center space-x-2">
                      <User className="h-4 w-4" />
                      <span>홀씨와 함께 시작하기</span>
                    </Link>
                  </Button>
                )}
              </div>
            </nav>
          </div>
        )}
      </div>
      
      <LazyProfileEditModal 
        isOpen={isProfileModalOpen} 
        onClose={() => setIsProfileModalOpen(false)} 
      />
    </header>
  );
};

export default Header;